import { ApptTemplate, ApptTemplateEntry } from 'types/apptTemplate';
import { formatHourMinutes } from 'utils/dateUtil';
import { TimePlannerState } from './types';

const applyOnlineLimit = (
  slot: { total_planned: number; staff_only_planned: number },
  online: number,
) => {
  const total = Math.max(slot.total_planned, online);
  return {
    total_planned: total,
    staff_only_planned: total - online,
  };
};

export const RESET_ONLINE_ACTION = ({ state }: { state: TimePlannerState }) => {
  const day: TimePlannerState['day'] = { ...state.day };
  day.staff_only_planned = 0;
  day.hours.forEach((hr) => {
    hr.staff_only_planned = 0;
    hr.intervals = hr.intervals.map((interval) => ({
      total_planned: interval.total_planned,
      staff_only_planned: 0,
      minute: interval.minute,
    }));
  });
  return day;
};

export const UPDATE_DAY_ACTION = ({
  state,
  value,
  staffOnly,
}: {
  state: TimePlannerState;
  value: number;
  staffOnly?: boolean;
}) => {
  const day: TimePlannerState['day'] = { ...state.day };
  if (staffOnly) {
    day.total_planned = value;
    if (day.staff_only_planned > value) {
      day.staff_only_planned = value;
    }
  } else {
    const next = applyOnlineLimit(day, value);
    day.total_planned = next.total_planned;
    day.staff_only_planned = next.staff_only_planned;
  }
  day.hours.forEach((_, hourIndex) => {
    UPDATE_HOUR_ACTION({ state, hourIndex, value, staffOnly });
  });
  return day;
};

export const UPDATE_HOUR_ACTION = ({
  state,
  hourIndex,
  value,
  staffOnly,
}: {
  state: TimePlannerState;
  hourIndex: number;
  value: number;
  staffOnly?: boolean;
}) => {
  const day: TimePlannerState['day'] = { ...state.day };
  const hour = day.hours[hourIndex];
  if (staffOnly) {
    hour.total_planned = value;
    if (hour.staff_only_planned > value) {
      hour.staff_only_planned = value;
    }
  } else {
    const next = applyOnlineLimit(hour, value);
    hour.total_planned = next.total_planned;
    hour.staff_only_planned = next.staff_only_planned;
  }
  hour.intervals.forEach((_, intervalIndex) =>
    UPDATE_INTERVAL_ACTION({
      state,
      value,
      staffOnly,
      intervalIndex,
      hourIndex,
    }),
  );
  return day;
};

const updateInterval = ({
  day,
  hourIndex,
  intervalIndex,
  value,
  staffOnly,
}: {
  day: TimePlannerState['day'];
  hourIndex: number;
  intervalIndex: number;
  value: number;
  staffOnly?: boolean;
}) => {
  const currentInterval = day.hours[hourIndex].intervals[intervalIndex];
  if (staffOnly) {
    day.hours[hourIndex].intervals[intervalIndex] = {
      total_planned: value,
      staff_only_planned:
        currentInterval.staff_only_planned > value
          ? value
          : currentInterval.staff_only_planned,
      minute: currentInterval.minute,
    };
  } else {
    const next = applyOnlineLimit(currentInterval, value);
    day.hours[hourIndex].intervals[intervalIndex] = {
      total_planned: next.total_planned,
      staff_only_planned: next.staff_only_planned,
      minute: currentInterval.minute,
    };
  }
};

export const UPDATE_INTERVAL_ACTION = ({
  state,
  ...rest
}: {
  state: TimePlannerState;
  hourIndex: number;
  intervalIndex: number;
  value: number;
  staffOnly?: boolean;
}) => {
  const day: TimePlannerState['day'] = { ...state.day };
  updateInterval({
    day: day,
    ...rest,
  });
  return day;
};

export const formatEntries = ({
  state,
  showOnline,
}: {
  state: TimePlannerState;
  showOnline: boolean;
}): ApptTemplateEntry[] => {
  return state.day.hours
    .map((hour) => {
      return hour.intervals.map((interval) => ({
        appt_time: formatHourMinutes(hour.hourNumber, interval.minute),
        total_planned: interval.total_planned,
        staff_only_planned: showOnline ? interval.staff_only_planned : 0,
      }));
    })
    .flat();
};

export const getIntervalEntry = ({
  timeTemplate,
  hourIndex,
  minutes,
}: {
  timeTemplate: ApptTemplate;
  hourIndex: number;
  minutes: number;
}) => {
  return timeTemplate.entries?.find(
    (entry) => formatHourMinutes(hourIndex, minutes) === entry.appt_time,
  );
};
